import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAppointments, removeAppointment, searchAppointments } from "../redux/slices/appointmentSlice";
import "../styles/TableStyles.css";

function AppointmentList({ onEdit, currentSection }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { appointments, loading, error } = useSelector((state) => state.appointments);
  const doctorId = useSelector((state) => state.auth.doctor?.doctorId);
  const isAdmin = useSelector((state) => state.admin.isAuthenticated);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isFocusedRef = useRef(false);
  const itemsPerPage = 10;

  React.useEffect(() => {
    // Fetch appointments on component mount
    // For admin, fetch all appointments; for doctor, fetch only their appointments
    const idToPass = isAdmin ? null : doctorId;
    dispatch(fetchAppointments(idToPass));
  }, [dispatch, doctorId, isAdmin]);

  // Maintain focus on search input while typing and after search results update
  useEffect(() => {
    if (inputRef.current && isFocusedRef.current) {
      inputRef.current.focus();
    }
  }, [searchQuery, appointments]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timeout for debounced search
    debounceTimerRef.current = setTimeout(() => {
      const idToPass = isAdmin ? null : doctorId;
      if (query.trim()) {
        dispatch(searchAppointments({ query, doctorId: idToPass }));
      } else {
        setCurrentPage(1);
        dispatch(fetchAppointments(idToPass));
      }
    }, 800); // 800ms debounce
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      dispatch(removeAppointment(id));
    }
  };

  const handleRowClick = (appointment) => {
    navigate(`/consultation/${appointment.appointment_id}`, { state: { appointment, activeSection: currentSection } });
  };

  // Calculate pagination
  const totalPages = Math.ceil(appointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = appointments.slice(startIndex, endIndex);

  const handleNextPage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) return <div className="loading">Loading appointments...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="table-container" ref={tableContainerRef}>
      <div className="search-container">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by patient name, phone, or status..."
          value={searchQuery}
          onChange={handleSearch}
          onFocus={() => { isFocusedRef.current = true; }}
          onBlur={() => { isFocusedRef.current = false; }}
          className="search-input"
        />
        {appointments.length > 0 && (
          <div className="pagination-controls">
            <button 
              onClick={handlePrevPage} 
              disabled={currentPage === 1}
              className="pagination-btn"
              type="button"
              title="Previous page"
            >
              &lt;
            </button>
            <span className="pagination-info">
              {currentPage}/{totalPages}
            </span>
            <button 
              onClick={handleNextPage} 
              disabled={currentPage >= totalPages}
              className="pagination-btn"
              type="button"
              title="Next page"
            >
              &gt;
            </button>
          </div>
        )}
      </div>
      {appointments.length === 0 ? (
        <p className="no-data">{inputRef.current?.value ? 'No appointments found' : 'No appointments found'}</p>
      ) : (
        <>
          {/* Top Table and Pagination */}

          <table className="modern-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Date</th>
                <th>Time</th>
                <th>Duration</th>
                <th>Room</th>
                <th>Status</th>
                <th>Fees</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAppointments.map((appointment) => (
                <tr 
                  key={appointment.appointment_id}
                  className="clickable-row"
                  onClick={() => handleRowClick(appointment)}
                >
                  <td>{appointment.patient_first_name} {appointment.patient_last_name}</td>
                  <td>{new Date(appointment.appointment_date).toLocaleDateString()}</td>
                  <td>{appointment.appointment_time}</td>
                  <td>{appointment.appointment_duration} mins</td>
                  <td>{appointment.room_number || "N/A"}</td>
                  <td>
                    <span className={`status-badge ${appointment.status}`}>
                      {appointment.status}
                    </span>
                  </td>
                  <td>
                    {appointment.fees ? `${parseFloat(appointment.fees).toFixed(2)}` : "—"}
                  </td>
                  <td className="remarks-cell" title={appointment.remarks}>
                    {appointment.remarks ? appointment.remarks.substring(0, 50) + (appointment.remarks.length > 50 ? "..." : "") : "—"}
                  </td>
                  <td className="action-buttons" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="delete-icon-btn"
                      onClick={(e) => handleDelete(e, appointment.appointment_id)}
                      title="Delete appointment"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Bottom Pagination Controls */}
          <div className="pagination-controls">
            <button 
              onClick={handlePrevPage} 
              disabled={currentPage === 1}
              className="pagination-btn"
              type="button"
              title="Previous page"
            >
              &lt;
            </button>
            <span className="pagination-info">
              {currentPage}/{totalPages}
            </span>
            <button 
              onClick={handleNextPage} 
              disabled={currentPage >= totalPages}
              className="pagination-btn"
              type="button"
              title="Next page"
            >
              &gt;
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AppointmentList;
